import { DomainError } from '@/core/errors/DomainError';
import type { Match, PhaseConfig, PhaseId, Tournament } from '@/core/schemas';

import type { MatchMutation, PhasePlan, Seed, StandingsTable } from './contracts';
import { requireFormat } from './registry';

function phaseRef(phase: PhaseConfig) {
  return { id: phase.id, label: phase.label };
}

function findPhase(tournament: Tournament, phaseId: PhaseId): PhaseConfig {
  const phase = tournament.settings.phases.find((candidate) => candidate.id === phaseId);

  if (phase === undefined) {
    throw new DomainError(
      'INVALID_STATE_TRANSITION',
      `Die Phase "${phaseId}" gehört nicht zu diesem Turnier.`,
      { phaseId },
    );
  }

  return phase;
}

/**
 * Drives the tournament through its phases without knowing a single format.
 *
 * Everything here talks to the format interface only. A tournament that runs
 * Swiss rounds instead of groups needs no change in this file.
 */

export function planPhase(
  tournament: Tournament,
  phaseId: PhaseId,
  participants: readonly Seed[],
): PhasePlan {
  const phase = findPhase(tournament, phaseId);
  const format = requireFormat(phase.formatId);

  return format.createPlan({
    phase: phaseRef(phase),
    config: phase.config,
    participants,
  });
}

export function standingsOf(
  tournament: Tournament,
  phaseId: PhaseId,
  participants: readonly Seed[],
  matches: readonly Match[],
): StandingsTable[] {
  const phase = findPhase(tournament, phaseId);
  const format = requireFormat(phase.formatId);

  return format.computeStandings({
    phase: phaseRef(phase),
    config: phase.config,
    participants,
    matches,
  });
}

export function progressionFor(
  tournament: Tournament,
  phaseId: PhaseId,
  matches: readonly Match[],
): MatchMutation[] {
  const phase = findPhase(tournament, phaseId);
  const format = requireFormat(phase.formatId);

  return format.resolveProgression({
    phase: phaseRef(phase),
    config: phase.config,
    matches,
  });
}

export function isPhaseComplete(
  tournament: Tournament,
  phaseId: PhaseId,
  matches: readonly Match[],
): boolean {
  const phase = findPhase(tournament, phaseId);
  const format = requireFormat(phase.formatId);

  return format.isComplete({ phase: phaseRef(phase), config: phase.config, matches });
}

export function qualifiersOf(
  tournament: Tournament,
  phaseId: PhaseId,
  participants: readonly Seed[],
  matches: readonly Match[],
): Seed[] {
  const phase = findPhase(tournament, phaseId);
  const format = requireFormat(phase.formatId);

  return format.selectQualifiers({
    phase: phaseRef(phase),
    config: phase.config,
    participants,
    matches,
  });
}

/** The phase that follows, or `null` when the tournament is over. */
export function nextPhaseId(tournament: Tournament, phaseId: PhaseId): PhaseId | null {
  const phases = tournament.settings.phases;
  const index = phases.findIndex((phase) => phase.id === phaseId);

  if (index === -1) {
    throw new DomainError(
      'INVALID_STATE_TRANSITION',
      `Die Phase "${phaseId}" gehört nicht zu diesem Turnier.`,
      { phaseId },
    );
  }

  return phases[index + 1]?.id ?? null;
}

export interface PhaseTransition {
  readonly completedPhaseId: PhaseId;
  readonly nextPhaseId: PhaseId | null;
  readonly qualifiers: readonly Seed[];
}

/**
 * Decides what happens once a phase is finished: who qualified and which phase
 * comes next. Returns `null` while the phase is still running, so callers can
 * ask after every result without checking first.
 */
export function advanceIfComplete(
  tournament: Tournament,
  phaseId: PhaseId,
  participants: readonly Seed[],
  matches: readonly Match[],
): PhaseTransition | null {
  if (!isPhaseComplete(tournament, phaseId, matches)) {
    return null;
  }

  return {
    completedPhaseId: phaseId,
    nextPhaseId: nextPhaseId(tournament, phaseId),
    qualifiers: qualifiersOf(tournament, phaseId, participants, matches),
  };
}
